import { Helmet } from 'react-helmet';
import {useContext, useEffect, useRef} from 'react';
import {Box, Grid2, Typography} from '@mui/material';
import Header from '../../layout/Header';
import Footer from '../../layout/Footer';
import CustomContext from "../../contexts/CustomContext";

export default function Cookies() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const context = useContext(CustomContext);

  useEffect(() => {
    if (context?.setIsLightBg) {
      context.setIsLightBg(true); // Force light background context
    }

    // Optional: Reset to false on unmount
    return () => {
      if (context?.setIsLightBg) {
        context.setIsLightBg(false);
      }
    };
  }, [context]);

  return (
    <Grid2
      container
      flexDirection="column"
      sx={{ position: 'relative', minHeight: '100vh', bgcolor: 'secondary.lighter', color: 'primary.main' }}
    >
      <Header />

      {/* MAIN SCROLLABLE CONTENT */}
      <Box component="main" sx={{ width: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Box ref={scrollContainerRef} sx={{ height: '100vh', scrollSnapType: 'y mandatory', scrollBehavior: 'smooth', overflowY: 'auto' }}>
          <Helmet>
            <title>Cookies - SHEKR</title>
            <meta name="description" content="Understand how we collect, use, and protect your data." />
          </Helmet>

          <Box sx={{ pt: '6rem', maxWidth: '800px', mx: 'auto', px: 2 }}>
            <Typography variant="h4" sx={{ mb: 3 }}><strong>Cookies</strong></Typography>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>1. INTRODUCTION</strong></Typography>
            <Typography paragraph><strong>Last Update</strong><br />Last updated on 9th December 2025.</Typography>
            <Typography paragraph><strong>Scope of Application</strong><br />We use cookies (and similar technologies like SDKs and pixels) to power SHEKR, support creators, and bring you content you’ll love. Some cookies are essential, others you can choose.</Typography>
            <Typography paragraph><strong>This policy explains:</strong></Typography>

            <Box sx={{ pl: 2 }}>
              <Typography paragraph>
                •	Why we use them (by category)<br />
                •	How third parties may use them<br />
                •	Your choices and rights
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>2. What Are Cookies ?</strong></Typography>
            <Typography paragraph>Cookies are small files stored on your device that help remember your preferences, improve performance, and personalize your experience. We also use local storage, SDKs, and tracking pixels—all referred to here as “cookies.”</Typography>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>3. Our Cookie Categories</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph><strong>a.	Essential (Always on)</strong></Typography>
              <Typography paragraph>These keep SHEKR running.</Typography>
              <Typography paragraph>What they do:</Typography>
              <Box sx={{ pl: 2 }}>
                <Typography paragraph>
                  •	Keep you logged in<br />
                  •	Secure the platform (anti-fraud, bot protection)<br />
                  •	Remember critical settings (language, cart, region)
                </Typography>
              </Box>
              <Typography paragraph>Why it's helpful:</Typography>
              <Typography paragraph>Without them, SHEKR wouldn't work properly your access, security, and basic experience depend on them.</Typography>
              <Typography paragraph><strong>b.	Experience (Optional – opt-in)</strong></Typography>
              <Typography paragraph>These cookies help us improve how SHEKR feels.</Typography>
              <Typography paragraph>What they do:</Typography>
              <Box sx={{ pl: 2 }}>
                <Typography paragraph>
                  •	Measure performance (app speed, crashes)<br />
                  •	Help us fix bugs and improve features<br />
                  •	Enable A/B testing for better flows
                </Typography>
              </Box>
              <Typography paragraph>Why it's helpful:</Typography>
              <Typography paragraph>They help shape a smoother, more responsive platform for you (and all of us using SHEKR).</Typography>
              <Typography paragraph><strong>c.	Personalisation (Optional – opt-in)</strong></Typography>
              <Typography paragraph>
                These let us show you products, creators, and content that actually match your vibe.
              </Typography>
              <Typography paragraph>
                What they do:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography paragraph>
                  •	Remember your product and creator preferences<br />
                  •	Personalise your feed and recommendations<br />
                  •	Track how you use features<br />
                  •	Measure engagement, retention<br />
                  •	Help us understand what works and what doesn’t
                </Typography>
              </Box>
              <Typography paragraph>Why it's helpful:</Typography>
              <Typography paragraph>These insights help us build features you actually enjoy and make your SHEKR feed feels more “you” tailored, clever, and valuable.</Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>4. Cookie Duration and Legal Exceptions</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>
                •	Session cookies: deleted when you close the app<br />
                •	Persistent cookies: last up to 13 months<br />
                •	We keep cookie data only for as long as needed to serve its purpose.<br />
                •	We may keep some cookie data longer if required for legal, fraud prevention, or regulatory reasons
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>5.	Your Choices & Settings</strong></Typography>
            <Typography paragraph>On first launch, you’ll see three options:</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>
                •	Get the best experience → activates all categories<br />
                •	Continue with limited experience → essential only<br />
                •	Set my experience → toggle Experience, Personalisation, Analytics, Advertising
              </Typography>
            </Box>
            <Typography paragraph>You can change your choices anytime in Settings → Privacy & Cookies.</Typography>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>6.	Your Rights under GDPR</strong></Typography>
            <Typography paragraph>You can:</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>
                •	Review what data is collected and why<br />
                •	Withdraw consent whenever<br />
                •	Request access, correction, deletion of your data
              </Typography>
            </Box>
            <Typography paragraph>Check our full Privacy Policy or email contact@shekr.com.</Typography>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>7.	Changes to This Policy</strong></Typography>
            <Typography paragraph>We may update over time (e.g. changing cookie vendors). We’ll notify you in-app or by email before changes take effect.</Typography>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>8.	Questions?</strong></Typography>
            <Typography paragraph>Reach us at contact@shekr.com We're happy to help!</Typography>
          </Box>

          <Footer />
        </Box>
      </Box>
    </Grid2>
  );
}
